// 17-question Roadmap definition. Order is meaningful (build instructions).
// The client's preferred name is now collected during account signup, so the
// old "What is your name?" question has been removed. The rest of the flow is
// untouched — only the visible progress numbering was renumbered 1..17.
import { MINDSET_PATTERN_OPTIONS, MINDSET_ROOT_OPTIONS } from "./mindset";

export const QUESTIONS = [
  {
    id: "basics",
    eyebrow: "Question 1 of 17",
    title: "Tell me a little about you.",
    helper: "Age, height (cm) and weight (kg). This is so we can create personalised targets for you, your body and goals.",
    type: "basics",
  },
  {
    id: "avg_steps",
    eyebrow: "Question 2 of 17",
    title: "How many steps do you average per day?",
    helper: "Check your phone or watch. Try to estimate an average across a normal week.",
    type: "number",
    field: "avg_steps",
    placeholder: "e.g. 6500",
    extra: { id: "steps_note", placeholder: "Anything to explain about the variation? (optional)", type: "textarea" },
  },
  {
    id: "workouts",
    eyebrow: "Question 3 of 17",
    title: "What workouts do you do outside of walking?",
    helper: "Tell me what, for how long, how intensely, and how many days per week.",
    type: "workouts",
  },
  {
    id: "bed_wake",
    eyebrow: "Question 4 of 17",
    title: "What's your typical bed and wake time?",
    helper: "Type the hour and minutes, then tap AM or PM. The note below helps us tailor your sleep habits.",
    type: "bed_wake",
  },
  {
    id: "last_two_hours",
    eyebrow: "Question 5 of 17",
    title: "What do you usually spend the last 2 hours doing, before lights out?",
    type: "textarea",
    placeholder: "Be honest. Scrolling, TV, work, kids, wine…",
  },
  {
    id: "sleep_hours",
    eyebrow: "Question 6 of 17",
    title: "How many hours do you sleep on most nights?",
    type: "choice",
    options: [
      { value: "<5", label: "Less than 5" },
      { value: "5-7", label: "5 to 7 hours" },
      { value: "7-9", label: "7 to 9 hours" },
      { value: "9+", label: "9 or more" },
    ],
  },
  {
    id: "broken_sleep",
    eyebrow: "Question 7 of 17",
    title: "How often do you get broken sleep?",
    type: "choice",
    options: [
      { value: "every_night", label: "Every night" },
      { value: "every_other_night", label: "Every other night" },
      { value: "few_times_week", label: "A few times a week" },
      { value: "few_times_month", label: "A few times a month" },
      { value: "never", label: "Never" },
    ],
  },
  {
    id: "energy_level",
    eyebrow: "Question 8 of 17",
    title: "How would you rate your daily energy levels out of 10?",
    type: "choice",
    options: [
      { value: "1-3", label: "1-3 I feel like a zombie" },
      { value: "3-5", label: "3-5 I'm the queen of surviving" },
      { value: "5-7", label: "5-7 Average energy but not great" },
      { value: "7-9", label: "7-9 Above average energy" },
      { value: "10",  label: "10 Energiser bunny ain't got nothing on meee" },
    ],
  },
  {
    id: "stress_level",
    eyebrow: "Question 9 of 17",
    title: "How would you rate your stress levels out of 10?",
    type: "choice",
    options: [
      { value: "1-3", label: "1-3 All Panic, No Disco" },
      { value: "3-5", label: "3-5 Survival Mode" },
      { value: "5-7", label: "5-7 A mix of feeling in and out of control" },
      { value: "7-9", label: "7-9 Feeling Zen for the most part" },
      { value: "10",  label: "10 Peak Peace" },
    ],
  },
  {
    id: "eating_habits",
    eyebrow: "Question 10 of 17",
    title: "What do your eating habits look like, and how often do cravings hit?",
    helper: "How many meals & snacks a day, plus when cravings show up.",
    type: "textarea",
    placeholder: "e.g. 3 meals + 2 snacks, sugar cravings around 3pm…",
  },
  {
    id: "emotional_eating",
    eyebrow: "Question 11 of 17",
    title: "How often do you struggle with emotional eating?",
    type: "choice",
    options: [
      { value: "daily",            label: "It's become almost a daily habit for me to cope with boredom, stress or other uncomfortable emotions/situations" },
      { value: "few_times_week",   label: "It happens a few times a week when I'm overwhelmed, I do it without even thinking" },
      { value: "few_times_month",  label: "It happens a few times a month, only when I'm very stressed" },
      { value: "never",            label: "I never emotionally eat or binge" },
    ],
  },
  {
    id: "mindset_patterns",
    eyebrow: "Question 12 of 17",
    title: "Historically, what have been the 2 biggest things that have prevented you getting consistent results and keeping those results long term?",
    helper: "Choose exactly 2.",
    type: "multi_choice_cards",
    minSelect: 2,
    maxSelect: 2,
    options: MINDSET_PATTERN_OPTIONS,
  },
  {
    id: "mindset_root",
    eyebrow: "Question 13 of 17",
    title: "Based on your answer to the previous question, what do you think is the number ONE driver underneath that pattern?",
    helper: "You might have more than one driver from this list, but choose the most dominant one.",
    type: "choice_cards",
    options: MINDSET_ROOT_OPTIONS,
  },
  {
    id: "me_time_current",
    eyebrow: "Question 14 of 17",
    title: "How much time do you give to yourself on a daily basis?",
    helper: "Just for you. Not chores, not the kids, not work, not social.",
    type: "text",
    placeholder: "e.g. 10 minutes / nothing right now",
  },
  {
    id: "me_time_realistic",
    eyebrow: "Question 15 of 17",
    title: "How much time can you realistically commit to yourself, daily?",
    type: "text",
    placeholder: "e.g. 90 mins most days",
  },
  {
    id: "main_goal",
    eyebrow: "Question 16 of 17",
    title: "What's your main goal, in your own words?",
    helper: "Describe exactly how you want to look and feel and what you want to experience daily as your new reality in 6 months time?",
    type: "textarea",
    placeholder: "Be specific e.g. I want to lose 15kg and keep it off. I want to feel confident having my picture taken, be consistent with my fitness routine and feel stronger and fitter",
  },
  {
    id: "pace",
    eyebrow: "Question 17 of 17",
    title: "What pace do you want to go at to achieve your fat loss results?",
    helperHtml:
      "<strong class=\"font-bold text-brand-ink\">Before you choose, please read this:</strong> It takes time to unlearn unhealthy coping habits &amp; patterns of behaviour and build newer aligned ones you can keep long term. You're here because you want this to be the last time you start. A bigger deficit will mean you lose fat faster, but it also means you need to be more structured and disciplined and sacrifice flexibility with your food choices, habits and routines. This is why I strongly recommend that if you have 20 kg or more to lose, a very fast pace is not wise for sustainability or long term consistency. Choose the option that feels sustainable for at least the next 6 months, not just the next few weeks.",
    type: "choice",
    options: [
      { value: "steady",  label: "Steady and enjoyable: I want a steady peaceful pace." },
      { value: "balance", label: "Happy balance: I want a slightly bigger deficit without sacrificing enjoyment and life." },
      { value: "fastest", label: "Fastest fat loss: I want to lose it as fast as I can without compromising my health. I understand this option will need more commitment to discipline and structure from me." },
    ],
  },
];
